import "./Courses.css";
import React, { useState, useEffect, useLayoutEffect, useCallback } from "react";
import logger from "../Components/Logger";
import { LevelName } from "../Components/LevelName";

const CACHE_KEY = "a1app_courses_cache";
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

export function Courses({ t }) {
  const [activePage, setActivePage] = useState("Elementary");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [coursesData, setCoursesData] = useState({
    Elementary: [],
    Highschool: [],
    Cegep: [],
  });

  // ── Parse and group raw API data ──
  const groupCourses = (data) => {
    const groupedData = { Elementary: [], Highschool: [], Cegep: [] };

    data.forEach((item) => {
      const level =
        item.level === "High School"
          ? "Highschool"
          : item.level === "College"
            ? "Cegep"
            : item.level;

      const course = {
        id: item.id,
        title: item.Title || item.subject || "",
        icon: item.icon || "📘",
        desc: item.Description || "",
        status: item.status || "active",
        details: {
          approach: item.OurApproche || "",
          subjects: item.SubjectsCovered || "",
        },
      };

      // Only include active courses with a valid level
      if (groupedData[level] && course.status === "active") {
        groupedData[level].push(course);
      }
    });

    return groupedData;
  };

  // ── Fetch courses with localStorage cache (1 hour TTL) ──
  const fetchCourses = useCallback(() => {
    // Try reading from cache first
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          logger.info("Courses loaded from localStorage cache — no Firestore read.");
          setCoursesData(groupCourses(data));
          setIsLoading(false);
          return; // ✅ Skip the network call entirely
        }
      }
    } catch (_) {}

    // Cache miss or expired — fetch from Firestore
    setIsLoading(true);
    fetch("/.netlify/functions/getAllCourses")
      .then((res) => {
        if (!res.ok) {
          logger.error("Failed to fetch courses, network response was not ok.");
          throw new Error("Network response was not ok");
        }
        return res.json();
      })
      .then((data) => {
        // Save raw data to cache
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
          logger.info("Courses fetched from Firestore and saved to cache.");
        } catch (_) {}

        setCoursesData(groupCourses(data));
      })
      .catch((err) => {
        logger.error("Erreur chargement cours:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  useEffect(() => {
    document.body.style.overflow = selectedCourse ? "hidden" : "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [selectedCourse]);

  useLayoutEffect(() => {
    const allowedLevels = ["Elementary", "Highschool", "Cegep"];
    const level = window.location.hash.replace("#", "");
    if (allowedLevels.includes(level)) {
      setActivePage(level);
      logger.info(`Active page set to ${level} based on URL hash`);
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const navItems = [
    { id: "Elementary", level: "elementary" },
    { id: "Highschool", level: "highschool" },
    { id: "Cegep", level: "cegep" },
  ];

  return (
    <div className="courses-container">
      <div className="courses-header">
        <h1>{t.TitleCoursesPage}</h1>
        <div className="underline"></div>
        <p>{t.AdmissionDescriptionCourses}</p>
      </div>

      <nav className="level-navigation">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`nav-link ${activePage === item.id ? "active" : ""}`}
            onClick={() => setActivePage(item.id)}
          >
            <LevelName level={item.level} />
          </button>
        ))}
      </nav>

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading classes...</p>
        </div>
      ) : (
        <div className="services-grid">
          {coursesData[activePage].length > 0 ? (
            coursesData[activePage].map((course) => (
              <div
                key={course.id}
                className="service-card"
                onClick={() => setSelectedCourse(course)}
              >
                <div className="icon-circle">{course.icon}</div>
                <h3>{course.title}</h3>
                <p>{course.desc}</p>
                <span className="read-more">{t.ClickForDetails}</span>
              </div>
            ))
          ) : (
            <p className="no-courses">No courses found.</p>
          )}
        </div>
      )}

      {selectedCourse && (
        <div className="modal-overlay" onClick={() => setSelectedCourse(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => setSelectedCourse(null)}>&times;</button>
            <h2>{selectedCourse.title}</h2>
            <hr />
            <div className="modal-details">
              {selectedCourse.details ? (
                <div>
                  <h4>{t.informationSupp}</h4>
                  <h3>{t.ApproachTitle}</h3>
                  <p>{selectedCourse.details.approach}</p>
                  <h3>{t.SubjectsTitle}</h3>
                  <ul className="modal-list">
                    {selectedCourse.details.subjects
                      ?.split(",")
                      .map((matiere, index) => (
                        <li key={index}>{matiere.trim()}</li>
                      ))}
                  </ul>
                </div>
              ) : (
                <p>{t.NoInformation}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}